import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Audio } from '../models/audio';
import { BookFeature } from '../models/book-feature';
import { Link } from '../models/link';
import { MoveableBookFeature } from '../models/moveable-book-feature';
import { PageBookFeatures } from '../models/page-book-features';
import { Video } from '../models/video';
import { ApplicationStateService } from './application-state.service';
import { BookAudioService } from './book-audio.service';
import { BookVideoService } from './book-video.service';
import { ChapterService } from './chapter.service';
import { FabricService } from './fabric.service';
import { LinkService } from './link.service';
import { PageFavoriteService } from './page-favorite.service';
import { PageTextService } from './page-text.service';
import { QuickLinkService } from './quick-link.service';
import { ReadspeakerService } from './readspeaker.service';
import { RectService } from './rect.service';
import { RestService } from './rest.service';
import { RouteService } from './route.service';
import { PageService } from './page.service';
import { EditMoveableBookFeatureEvent } from '../utils/events/edit-moveable-book-feature-event';
import { CommunicationService } from './communication.service';

@Injectable({
    providedIn: 'root'
})
export class BookFeatureService {

    constructor(private linkService: LinkService,
                private rectService: RectService,
                private videoService: BookVideoService,
                private audioService: BookAudioService,
                private fabricService: FabricService,
                private pageFavoriteService: PageFavoriteService,
                private readspeakerService: ReadspeakerService,
                private chapterService: ChapterService,
                private restService: RestService,
                private routeService: RouteService,
                private pageTextService: PageTextService,
                private quickLinkService: QuickLinkService,
                private applicationStateService: ApplicationStateService,
                private communicationService: CommunicationService,
                private pageService: PageService) {
    }

    getAllBookFeaturesOfAllPages(): Promise<any> {

        return new Promise<any>(resolve => {

            const route = this.routeService.getRelativeRoute(environment.endpointUrls.bookFeatures,
                {
                    bookId: this.applicationStateService.bookId
                });

            this.restService.get(route)
                .then(result => {
                    const viewablePagesIDs = this.pageService.getGetViewablePagesIDs();

                    this.chapterService.setObjectsToChapters(result.chapters);

                    this.linkService.setObjectsToLinks(this.filterViewable(result.links, viewablePagesIDs));
                    this.videoService.setObjectsToVideos(this.filterViewable(result.bookVideos, viewablePagesIDs));
                    this.audioService.setObjectsToAudios(this.filterViewable(result.bookAudios, viewablePagesIDs));
                    this.rectService.setObjectsToRects(this.filterViewable(result.rects, viewablePagesIDs));
                    this.pageFavoriteService.setObjectsToLinks(this.filterViewable(result.pageFavorites, viewablePagesIDs));
                    this.pageTextService.setObjectsToTextSelections(this.filterViewable(result.textSelections, viewablePagesIDs));
                    this.fabricService.setObjectsToPageDrawings(this.filterViewable(result.pageDrawings, viewablePagesIDs));

                    resolve(null);
                });
        });
    }

    getBookFeaturesOfAllPages(): Promise<Array<MoveableBookFeature>> {

        return new Promise<Array<MoveableBookFeature>>((resolve, reject) => {

            let bookFeatures = [];

            Promise.all([
                this.videoService.getVideos(),
                this.audioService.getAudios(),
                this.rectService.getRects(),
                this.linkService.getLinks()
            ])
                .then(values => {

                    values.forEach(value => {
                        bookFeatures = bookFeatures.concat(value);
                    });
                    resolve(bookFeatures);
                })
                .catch(err => {
                    reject(err);
                });
        });
    }

    getBookFeaturesOfAllPages2(): Promise<PageBookFeatures> {

        return new Promise<PageBookFeatures>((resolve, reject) => {

            const features = new PageBookFeatures();

            Promise.all([
                this.videoService.getVideos(),
                this.audioService.getAudios(),
                this.rectService.getRects(),
                this.linkService.getLinks()
            ])
                .then((values: any) => {

                    features.videos = values[0];
                    features.audios = values[1];
                    features.rects = values[2];
                    features.links = values[3];

                    resolve(features);
                })
                .catch(err => {
                    reject(err);
                });
        });
    }

    getAllBookFeaturesOfPage(pageIndex: number): Promise<Array<MoveableBookFeature>> {

        return new Promise<Array<MoveableBookFeature>>(resolve => {

            let bookFeatures = [];

            Promise.all([
                this.readspeakerService.getReadspeakerElementsOfPage(pageIndex),
                this.videoService.getVideosOfPage(pageIndex),
                this.audioService.getAudiosOfPage(pageIndex),
                this.rectService.getRectsOfPage(pageIndex),
                this.linkService.getLinksOfPage(pageIndex)
            ])
                .then(values => {

                    values.forEach(value => {
                        bookFeatures = bookFeatures.concat(value);
                    });
                    resolve(bookFeatures);
                });
        });
    }

    getAllBookFeaturesOfPage2(pageIndex: number): Promise<PageBookFeatures> {

        return new Promise<PageBookFeatures>(resolve => {

            const features = new PageBookFeatures();

            Promise.all([
                this.readspeakerService.getReadspeakerElementsOfPage(pageIndex),
                this.videoService.getVideosOfPage(pageIndex),
                this.audioService.getAudiosOfPage(pageIndex),
                this.rectService.getRectsOfPage(pageIndex),
                this.linkService.getLinksOfPage(pageIndex)
            ])
                .then((values: any) => {

                    features.readspeakerElements = values[0];
                    features.videos = values[1];
                    features.audios = values[2];
                    features.rects = values[3];
                    features.links = values[4];
                    features.pageDrawing = this.fabricService.getPageDrawingOfPageByIndex(pageIndex);

                    resolve(features);
                });
        });
    }

    getInteractiveFeaturesOfAllPages(): Promise<Array<MoveableBookFeature>> {

        return new Promise<Array<MoveableBookFeature>>((resolve, reject) => {

            let bookFeatures = [];

            Promise.all([
                this.videoService.getVideos(),
                this.audioService.getAudios(),
                this.linkService.getLinks()
            ])
                .then(values => {

                    values.forEach(value => {
                        bookFeatures = bookFeatures.concat(value);
                    });
                    resolve(bookFeatures);
                })
                .catch(err => {
                    reject(err);
                });
        });
    }

    getInteractiveFeaturesOfAllPages2(): Promise<PageBookFeatures> {

        return new Promise<PageBookFeatures>((resolve, reject) => {

            const features = new PageBookFeatures();

            Promise.all([
                this.videoService.getVideos(),
                this.audioService.getAudios(),
                this.linkService.getLinks()
            ])
                .then((values: any) => {
                    features.videos = values[0];
                    features.audios = values[1];
                    features.links = values[2];
                    resolve(features);
                })
                .catch(err => {
                    reject(err);
                });
        });

    }

    /**
     * Just fetches the BookFeatures which do not access the backend.
     * @param {number} pageIndex
     * @returns {Promise<MoveableBookFeature[]>}
     */
    getCachedBookFeaturesOfPage(pageIndex: number): Promise<Array<MoveableBookFeature>> {

        return new Promise<Array<MoveableBookFeature>>(resolve => {

            let bookFeatures = [];

            Promise.all([
                this.videoService.getVideosOfPage(pageIndex),
                this.audioService.getAudiosOfPage(pageIndex),
                this.rectService.getRectsOfPage(pageIndex),
                this.linkService.getLinksOfPage(pageIndex)

            ])
                .then(values => {

                    values.forEach(value => {
                        bookFeatures = bookFeatures.concat(value);
                    });
                    resolve(bookFeatures);
                });
        });
    }

    getCachedBookFeaturesOfPage2(pageIndex: number): Promise<PageBookFeatures> {

        return new Promise<PageBookFeatures>(resolve => {

            const features = new PageBookFeatures();

            Promise.all([
                this.videoService.getVideosOfPage(pageIndex),
                this.audioService.getAudiosOfPage(pageIndex),
                this.rectService.getRectsOfPage(pageIndex),
                this.linkService.getLinksOfPage(pageIndex),
                // eslint-disable-next-line @typescript-eslint/await-thenable
                this.quickLinkService.getAllQuickLinksOfPageIndex(pageIndex),
                this.readspeakerService.getReadspeakerElementsOfPage(pageIndex)
            ])
                .then((values: any) => {

                    features.videos = values[0];
                    features.audios = values[1];
                    features.rects = values[2];
                    features.links = values[3];
                    features.quickLinks = values[4];
                    features.readspeakerElements = values[5];
                    features.pageDrawing = this.fabricService.getPageDrawingOfPageByIndex(pageIndex);

                    resolve(features);
                });
        });
    }

    /**
     * checks if the book has any feature of the given type
     * default fallback is for all interactive features (MoveableBookFeatures)
     *
     * @param {Function} classConstructor
     * @returns {Promise<boolean>}
     */
    hasFeaturesOfType(classConstructor: any): Promise<boolean> {

        switch (classConstructor.name) {

            case Link.constructor.name:

                return this.linkService.getLinks()
                    .then(values => this.hasValuesPromise(values));

            case Audio.constructor.name:

                return this.videoService.getVideos()
                    .then(values => this.hasValuesPromise(values));

            case Video.constructor.name:

                return this.audioService.getAudios()
                    .then(values => this.hasValuesPromise(values));

            default:
                return this.hasInteractiveFeatures();
        }
    }

    private filterViewable(elements: Array<any>, viewableIDs: Array<number>): Array<any> {
        if (viewableIDs) {
            return elements.filter(el => viewableIDs.includes(el.pageId));
        }

        return elements;
    }

    /**
     * checks if the book has any interactive features (also quick links)
     *
     * @returns {Promise<boolean>}
     */
    private hasInteractiveFeatures(): Promise<boolean> {
        return new Promise(resolve => {
            if (this.hasQuickLinks()) {
                resolve(true);

                return;
            }

            const result = this.getInteractiveFeaturesOfAllPages()
                .then(values => values.length > 0);

            resolve(result);
        });
    }

    /**
     * Check if the book has QuickLinks.
     *
     * @returns {boolean}
     */
    private hasQuickLinks(): boolean {
        const bookFeatures = this.applicationStateService.bookConfig.quickLinksAvailability;
        if (!bookFeatures) {
            return false;
        }

        return bookFeatures.generic ||
            bookFeatures.eSquirrel ||
            bookFeatures.gmt ||
            bookFeatures.hsm;
    }

    /**
     * get boolean promise
     * true: if values contains at least one element
     * false: otherwise
     *
     * @param {Array<BookFeature>} values
     * @returns {Promise<boolean>}
     */
    private hasValuesPromise(values: Array<BookFeature>): Promise<boolean> {
        return new Promise(resolve => {
            resolve(values.length > 0);
        });
    }

    /**
     * closes open bookviewer tool
     */
    dismissMode(): void {
            this.fabricService.setCurrentDrawAction(undefined);
            this.applicationStateService.isPageEditable = false;
            this.applicationStateService.pageEditMode = undefined;
            this.communicationService.announceMoveableBookFeatureEdit(new EditMoveableBookFeatureEvent());
    }

    /**
     * tools for which pagelinks in bookviewer are disabled
     */
    get pageLinksDisabled(): boolean {
        return (
            this.applicationStateService.pageEditMode?.type === 'DRAW'||
            this.applicationStateService.pageEditMode?.type === 'SELECTION' ||
            this.applicationStateService.pageEditMode?.type === 'RECT'
        )
    }
}
